import { useEffect, useRef } from "react";

/**
 * Fire when the tab regains focus after having lost it.
 *
 * TanStack Query's refetchOnWindowFocus covers the queries; this covers the explicit
 * invalidation of the set that a Stripe handoff can change, and it exists as its own hook
 * so the assertion in the E2E path has one place to point at.
 *
 * There is deliberately NO success toast on return. The changed value IS the confirmation;
 * a toast claiming "saved" for a change made on another site would be a guess.
 */
export function useHandoffReturn(onReturn: () => void): void {
  const left = useRef(false);
  const cb = useRef(onReturn);
  cb.current = onReturn;

  useEffect(() => {
    const onBlur = () => {
      left.current = true;
    };
    const onFocus = () => {
      if (!left.current) return;
      left.current = false;
      cb.current();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") onBlur();
      else onFocus();
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
